import { Loading } from '@affine/component';
import { WorkspaceServerService } from '@affine/core/modules/cloud';
import { OrganizeService } from '@affine/core/modules/organize';
// Reuse styles from permissions
import { objectRoleLabel } from '@affine/core/modules/permissions/utils/object-role-labels';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { getUserFolderGrantsQuery } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { FolderIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect, useState } from 'react';

import * as styles from '../permissions/styles.css';

interface UserFoldersListProps {
  userId: string;
  userName: string;
  userPermission: string; // Workspace permission level (Owner, Admin, etc.)
}

interface FolderAccess {
  folderId: string;
  role: string;
  name: string;
  isAreaBlocked?: boolean;
  areaBoundaryKind?: string | null;
  areaBoundaryRootFolderId?: string | null;
  areaBoundaryRootName?: string | null;
}

const getAreaBlockedNotice = (
  t: ReturnType<typeof useI18n>,
  entry: FolderAccess
) => {
  if (!entry.isAreaBlocked) {
    return null;
  }
  const areaName = entry.areaBoundaryRootName;
  if (entry.areaBoundaryKind === 'private' && areaName) {
    return t['com.streamlain.folder-share.area-blocked.private']({ areaName });
  }
  if (entry.areaBoundaryKind === 'teamspace' && areaName) {
    return t['com.streamlain.folder-share.area-blocked.teamspace']({
      areaName,
    });
  }
  return t['com.streamlain.folder-share.area-blocked.generic']();
};

export const UserFoldersList = ({
  userId,
  userName: _userName,
  userPermission: _userPermission,
}: UserFoldersListProps) => {
  const t = useI18n();
  const workspaceService = useService(WorkspaceService);
  const serverService = useService(WorkspaceServerService);
  const organizeService = useService(OrganizeService);
  const folderTree = organizeService.folderTree;
  const rootFolder = folderTree.rootFolder;
  const children = useLiveData(rootFolder.sortedChildren$);
  const workspaceId = workspaceService.workspace.id;

  const [explicitGrants, setExplicitGrants] = useState<FolderAccess[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadUserFolders = useCallback(async () => {
    if (!serverService.server) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      const result = await serverService.server.gql({
        query: getUserFolderGrantsQuery,
        variables: {
          workspaceId,
          userId,
        },
      });

      const grants = (result as any).workspace?.userFolderGrants ?? [];

      // Build folder name map from Y.js
      const folderNameMap = new Map<string, string>();
      const collectNames = (nodes: typeof children) => {
        for (const node of nodes) {
          if (node.type$.value === 'folder' && node.id) {
            folderNameMap.set(node.id, node.name$.value || 'Unnamed Folder');
            collectNames(node.sortedChildren$.value);
          }
        }
      };
      collectNames(children);

      const enrichedGrants: FolderAccess[] = grants.map(
        (grant: {
          folderId: string;
          role: string;
          isAreaBlocked?: boolean | null;
          areaBoundaryKind?: string | null;
          areaBoundaryRootFolderId?: string | null;
          areaBoundaryRootName?: string | null;
        }) => ({
          folderId: grant.folderId,
          role: grant.role,
          name: folderNameMap.get(grant.folderId) || 'Unnamed Folder',
          isAreaBlocked: grant.isAreaBlocked ?? false,
          areaBoundaryKind: grant.areaBoundaryKind ?? null,
          areaBoundaryRootFolderId: grant.areaBoundaryRootFolderId ?? null,
          areaBoundaryRootName: grant.areaBoundaryRootName ?? null,
        })
      );

      setExplicitGrants(enrichedGrants);
    } catch (err) {
      console.debug('Failed to load user folder grants:', err);
      setExplicitGrants([]);
    } finally {
      setIsLoading(false);
    }
  }, [serverService.server, workspaceId, userId, children]);

  useEffect(() => {
    void loadUserFolders();
  }, [loadUserFolders]);

  if (isLoading) {
    return (
      <div className={styles.loadingState} style={{ padding: '12px' }}>
        <Loading size={16} />
      </div>
    );
  }

  // Show explicit grants for regular members
  if (explicitGrants.length === 0) {
    return (
      <div
        className={styles.emptyState}
        style={{ padding: '12px', fontSize: '13px' }}
      >
        {t['com.streamlain.settings.workspace.folders.user-no-folders']?.() ??
          'No specific folder permissions. Uses default workspace access.'}
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          fontSize: '12px',
          color: 'var(--streamlain-text-secondary-color)',
          marginBottom: '8px',
        }}
      >
        {t[
          'com.streamlain.settings.workspace.folders.user-folder-access'
        ]?.() ?? 'Folders with specific permissions'}
      </div>
      {explicitGrants.map(grant => {
        const areaBlockedNotice = getAreaBlockedNotice(t, grant);

        return (
          <div
            key={grant.folderId}
            className={styles.permissionRowWrapper}
            data-area-blocked={grant.isAreaBlocked ? 'true' : undefined}
          >
            <div
              className={`${styles.permissionRow} ${
                grant.isAreaBlocked ? styles.areaBlockedPermissionRow : ''
              }`}
            >
              <div className={styles.permissionUser}>
                <FolderIcon style={{ width: 16, height: 16, flexShrink: 0 }} />
                <div className={styles.listItemContent}>
                  <div className={styles.listItemTitle}>{grant.name}</div>
                </div>
              </div>
              <span className={styles.permissionRole}>
                {objectRoleLabel(t, grant.role)}
              </span>
            </div>
            {areaBlockedNotice ? (
              <div className={styles.areaBlockedNotice}>
                {areaBlockedNotice}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};
